import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSpinner,
  faWandMagicSparkles,
  faArrowRotateRight,
  faCheck,
  faPlus,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import flashcardService, { isQuotaError } from '../../services/flashcardService';
import UpgradePrompt from './UpgradePrompt';
import './Flashcards.css';

const buildTagsPrefill = (question) => {
  if (!question) return '';
  const name =
    question.categoryName ||
    (question.category && question.category.name) ||
    (Array.isArray(question.categories) && question.categories[0] && question.categories[0].name) ||
    (Array.isArray(question.questionCategories) &&
      question.questionCategories[0] &&
      question.questionCategories[0].category &&
      question.questionCategories[0].category.name) ||
    '';
  return name ? String(name).toLowerCase() : '';
};

const FALLBACK_ENTITLEMENT = {
  canCreate: false,
  canReview: false,
  studyDailyLimit: null,
  studyRemainingToday: null,
  requiredPlans: ['Standard', 'Premium'],
};

const formatAiTags = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.map((t) => String(t).trim()).filter(Boolean);
  return String(tags)
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
};

/**
 * "Create Flashcard from practice question" modal — opened from the
 * per-question toolbar in PracticeTest.js. See docs/flashcards-feature-spec.md
 * §2.5 / §7.2 for the full UX contract.
 */
const CreateFlashcardModal = ({ question, onClose }) => {
  const [entitlement, setEntitlement] = useState(null);
  const [entitlementLoading, setEntitlementLoading] = useState(true);

  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [tags, setTags] = useState(() => buildTagsPrefill(question));
  const [note, setNote] = useState('');
  const [aiGenerated, setAiGenerated] = useState(false);

  const [decks, setDecks] = useState([]);
  const [decksLoading, setDecksLoading] = useState(false);
  const [deckId, setDeckId] = useState('');

  const [showNewDeckForm, setShowNewDeckForm] = useState(false);
  const [newDeckName, setNewDeckName] = useState('');
  const [newDeckSaving, setNewDeckSaving] = useState(false);
  const [newDeckError, setNewDeckError] = useState(null);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [aiQuotaBlocked, setAiQuotaBlocked] = useState(false);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    flashcardService
      .getEntitlement()
      .then((data) => {
        if (active) setEntitlement(data || FALLBACK_ENTITLEMENT);
      })
      .catch(() => {
        if (active) setEntitlement(FALLBACK_ENTITLEMENT);
      })
      .finally(() => {
        if (active) setEntitlementLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const canCreate = !!(entitlement && entitlement.canCreate);

  const loadDecks = useCallback(() => {
    setDecksLoading(true);
    flashcardService
      .getDecks({ scope: 'Mine', pageSize: 100 })
      // `scope=Mine` also returns public decks the user bookmarked (snapshots),
      // which can't take new cards — keep only the user's own working decks.
      .then((res) =>
        setDecks((res?.items || []).filter((d) => d.isOwner && !d.isSnapshot))
      )
      .catch(() => setDecks([]))
      .finally(() => setDecksLoading(false));
  }, []);

  useEffect(() => {
    if (canCreate) loadDecks();
  }, [canCreate, loadDecks]);

  // Auto-close shortly after a successful save so the user sees confirmation.
  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => onClose(), 900);
    return () => clearTimeout(t);
  }, [success, onClose]);

  const handleGenerate = async (regenerate) => {
    if (!question || !question.id) return;
    setAiLoading(true);
    setAiError(null);
    setAiQuotaBlocked(false);
    try {
      const result = await flashcardService.generateAICard(question.id, regenerate);
      setAiDraft(result);
    } catch (err) {
      if (isQuotaError(err)) {
        setAiQuotaBlocked(true);
      } else {
        setAiError('Could not generate a flashcard right now. Please try again.');
      }
    } finally {
      setAiLoading(false);
    }
  };

  const handleUseThis = () => {
    if (!aiDraft) return;
    setFront(aiDraft.front || '');
    setBack(aiDraft.back || '');
    setTags(formatAiTags(aiDraft.tags).join(', '));
    setAiGenerated(true);
  };

  const handleNewDeckSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!newDeckName.trim()) {
      setNewDeckError('Give your deck a name.');
      return;
    }
    setNewDeckSaving(true);
    setNewDeckError(null);
    try {
      const created = await flashcardService.createDeck({
        name: newDeckName.trim(),
        description: '',
        color: '#6366f1',
        difficulty: 1,
      });
      setDecks((prev) => [created, ...prev.filter((d) => d.id !== created?.id)]);
      if (created && created.id != null) setDeckId(String(created.id));
      setShowNewDeckForm(false);
      setNewDeckName('');
    } catch (err) {
      if (isQuotaError(err)) {
        setNewDeckError('Upgrade your plan to create new decks.');
      } else {
        setNewDeckError('Could not create the deck. Please try again.');
      }
    } finally {
      setNewDeckSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!front.trim() || !back.trim()) {
      setFormError('Front and back are both required.');
      return;
    }
    if (!deckId) {
      setFormError('Choose a deck to save this card into.');
      return;
    }
    setSaving(true);
    try {
      await flashcardService.createCard(Number(deckId), {
        front: front.trim(),
        back: back.trim(),
        tags: tags.trim(),
        note: note.trim(),
        questionId: question ? question.id : null,
        aiGenerated,
      });
      setSuccess(true);
    } catch (err) {
      if (isQuotaError(err)) {
        setFormError('Upgrade your plan to create flashcards.');
      } else {
        setFormError('Could not save the flashcard. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fc-modal-overlay" onClick={onClose}>
      <div
        className="fc-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Create Flashcard"
      >
        <div className="fc-modal-header">
          <h2>Create Flashcard</h2>
          <button type="button" className="fc-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {entitlementLoading ? (
          <div className="fc-modal-body">
            <div className="fc-state">
              <FontAwesomeIcon icon={faSpinner} className="fc-spin fc-state-icon" />
            </div>
          </div>
        ) : success ? (
          <div className="fc-modal-body">
            <div className="fc-create-success">
              <FontAwesomeIcon icon={faCircleCheck} className="fc-create-success-icon" />
              <h3>Flashcard created</h3>
              <p>It's ready to study from your deck.</p>
            </div>
          </div>
        ) : !canCreate ? (
          <div className="fc-modal-body">
            <div className="fc-ai-teaser">
              <h3>Turn this question into a flashcard</h3>
              <p>
                Generate a spaced-repetition flashcard from any practice question with one
                click — AI drafts the front, back, and tags for you, or write your own by
                hand — then study it with real SM-2 scheduling.
              </p>
            </div>
            <UpgradePrompt
              variant="create"
              requiredPlans={entitlement?.requiredPlans}
              onDismiss={onClose}
              dismissLabel="Not now"
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="fc-modal-body">
              {/* --- AI generation section ------------------------------------ */}
              <div className="fc-ai-section">
                <div className="fc-ai-section-head">
                  <FontAwesomeIcon icon={faWandMagicSparkles} className="fc-ai-section-icon" />
                  <div>
                    <h3>Generate Flashcard using AI</h3>
                    <p>Use AI to automatically create a high-quality flashcard from this question.</p>
                  </div>
                </div>

                {!aiDraft && !aiLoading && !aiQuotaBlocked && (
                  <button
                    type="button"
                    className="fc-btn fc-btn-primary fc-ai-generate-btn"
                    onClick={() => handleGenerate(false)}
                    disabled={!question || !question.id}
                  >
                    <FontAwesomeIcon icon={faWandMagicSparkles} /> Generate with AI
                  </button>
                )}

                {aiLoading && (
                  <div className="fc-ai-loading">
                    <FontAwesomeIcon icon={faSpinner} className="fc-spin" /> Generating flashcard…
                  </div>
                )}

                {aiQuotaBlocked && (
                  <UpgradePrompt
                    variant="create"
                    requiredPlans={entitlement?.requiredPlans}
                    className="fc-upgrade-compact"
                  />
                )}

                {aiError && !aiQuotaBlocked && <div className="fc-error-banner">{aiError}</div>}

                {aiDraft && !aiLoading && (
                  <div className="fc-ai-preview">
                    {aiDraft.fromCache && (
                      <div className="fc-ai-cache-hint">
                        From a previously generated draft for this question
                      </div>
                    )}
                    <div className="fc-ai-preview-field">
                      <span className="fc-ai-preview-label">Front</span>
                      <p>{aiDraft.front}</p>
                    </div>
                    <div className="fc-ai-preview-field">
                      <span className="fc-ai-preview-label">Back</span>
                      <p>{aiDraft.back}</p>
                    </div>
                    {formatAiTags(aiDraft.tags).length > 0 && (
                      <div className="fc-ai-preview-tags">
                        {formatAiTags(aiDraft.tags).map((t) => (
                          <span key={t} className="fc-ai-tag">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="fc-ai-preview-actions">
                      <button
                        type="button"
                        className="fc-btn fc-btn-ghost fc-btn-sm"
                        onClick={() => handleGenerate(true)}
                        disabled={aiLoading}
                      >
                        <FontAwesomeIcon icon={faArrowRotateRight} /> Regenerate
                      </button>
                      <button
                        type="button"
                        className="fc-btn fc-btn-success fc-btn-sm"
                        onClick={handleUseThis}
                      >
                        <FontAwesomeIcon icon={faCheck} /> Use This
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <hr className="fc-divider" />

              {/* --- Manual form ------------------------------------------------ */}
              {formError && <div className="fc-error-banner">{formError}</div>}

              <div className="fc-field">
                <label className="fc-label" htmlFor="fc-card-front">
                  Front (Question)
                </label>
                <textarea
                  id="fc-card-front"
                  className="fc-textarea"
                  maxLength={2000}
                  value={front}
                  onChange={(e) => setFront(e.target.value)}
                  placeholder="A short, focused question (or use Generate with AI)"
                />
              </div>

              <div className="fc-field">
                <label className="fc-label" htmlFor="fc-card-back">
                  Back (Answer)
                </label>
                <textarea
                  id="fc-card-back"
                  className="fc-textarea"
                  maxLength={4000}
                  value={back}
                  onChange={(e) => setBack(e.target.value)}
                  placeholder="The key answer — keep it brief"
                />
              </div>

              <div className="fc-field">
                <label className="fc-label" htmlFor="fc-card-tags">
                  Tags
                </label>
                <input
                  id="fc-card-tags"
                  className="fc-input"
                  type="text"
                  maxLength={500}
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="Add tags (comma separated)"
                />
              </div>

              <div className="fc-field">
                <label className="fc-label" htmlFor="fc-card-deck">
                  Deck
                </label>
                <div className="fc-deck-select-row">
                  <select
                    id="fc-card-deck"
                    className="fc-select"
                    value={deckId}
                    onChange={(e) => setDeckId(e.target.value)}
                  >
                    <option value="" disabled>
                      {decksLoading ? 'Loading decks…' : 'Select a deck'}
                    </option>
                    {decks.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="fc-btn fc-btn-ghost fc-btn-sm"
                    onClick={() => setShowNewDeckForm((s) => !s)}
                  >
                    <FontAwesomeIcon icon={faPlus} /> New deck
                  </button>
                </div>

                {showNewDeckForm && (
                  <div className="fc-new-deck-inline">
                    <div className="fc-field">
                      <label className="fc-label" htmlFor="fc-newdeck-name">
                        New deck name
                      </label>
                      <input
                        id="fc-newdeck-name"
                        className="fc-input"
                        type="text"
                        maxLength={100}
                        value={newDeckName}
                        onChange={(e) => setNewDeckName(e.target.value)}
                        placeholder="e.g. Cardiology High-Yield"
                        autoFocus
                      />
                    </div>
                    {newDeckError && <div className="fc-error-banner">{newDeckError}</div>}
                    <div className="fc-new-deck-inline-actions">
                      <button
                        type="button"
                        className="fc-btn fc-btn-ghost fc-btn-sm"
                        onClick={() => {
                          setShowNewDeckForm(false);
                          setNewDeckError(null);
                        }}
                        disabled={newDeckSaving}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="fc-btn fc-btn-primary fc-btn-sm"
                        onClick={handleNewDeckSubmit}
                        disabled={newDeckSaving}
                      >
                        {newDeckSaving && <FontAwesomeIcon icon={faSpinner} className="fc-spin" />}
                        Create Deck
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="fc-field">
                <label className="fc-label" htmlFor="fc-card-note">
                  Note
                </label>
                <textarea
                  id="fc-card-note"
                  className="fc-textarea"
                  maxLength={1000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Additional notes…"
                />
              </div>
            </div>

            <div className="fc-modal-footer">
              <button
                type="button"
                className="fc-btn fc-btn-ghost"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button type="submit" className="fc-btn fc-btn-primary" disabled={saving}>
                {saving && <FontAwesomeIcon icon={faSpinner} className="fc-spin" />}
                Create Flashcard
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateFlashcardModal;
